import { Fragment, useEffect, useState, useCallback, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { Home, User, Bell, LogOut, FileText, Settings, Shield, Podcast, Search, SquarePen, X } from "lucide-react";
import { ScrollSymbol, ScrollLockup } from "@/components/ScrollSymbol";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { getDraftCount } from "@/lib/drafts";

type NavItem = {
  href: string;
  label: string;
  icon?: typeof Home;
  renderIcon?: () => ReactNode;
  match: (loc: string) => boolean;
  badge?: number;
};

interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileSidebar({ isOpen, onClose }: MobileSidebarProps) {
  const [location] = useLocation();
  const { user, signOut } = useAuth();
  const [draftCount, setDraftCount] = useState(0);

  const refreshDraftCount = useCallback(() => {
    setDraftCount(getDraftCount());
  }, []);

  useEffect(() => {
    refreshDraftCount();
    window.addEventListener("draftsChanged", refreshDraftCount);
    return () => window.removeEventListener("draftsChanged", refreshDraftCount);
  }, [refreshDraftCount]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const profilePath = "/profile";
  const fallbackInitial = (user?.displayName || user?.username || "?").slice(0, 1).toUpperCase();
  const badgeAccentStyle = { backgroundColor: user?.accentColor || "#1D1916" };

  const navItems: NavItem[] = [
    { href: "/home", label: "Home", icon: Home, match: (loc) => loc === "/home" },
    { href: "/podcasts", label: "Podcasts", icon: Podcast, match: (loc) => loc.startsWith("/podcast") },
    { href: profilePath, label: "Profile", icon: User, match: (loc) => loc.startsWith("/profile") },
    { href: "/activity", label: "Activity", icon: Bell, match: (loc) => loc === "/activity" },
    {
      href: "/settings/scroll-pro",
      label: "Scroll Pro",
      renderIcon: () => <ScrollSymbol size={13} />,
      match: (loc) => loc === "/settings/scroll-pro",
    },
    { href: "/settings", label: "Settings", icon: Settings, match: (loc) => loc.startsWith("/settings") && loc !== "/settings/scroll-pro" },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 sidebar-backdrop transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-[280px] flex flex-col bg-(--butter-bg-elevated) butter-shadow-panel rounded-r-2xl transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-5">
          <div className="text-(--scroll-text)">
            <ScrollLockup preset="nav" />
          </div>
          <button
            onClick={onClose}
            className="text-(--scroll-text-tertiary) hover:text-(--scroll-text) transition-colors p-1.5 rounded-lg"
            aria-label="Close menu"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item, idx) => {
            const active = item.match(location);
            const searchActive = location === "/search" || location === "/discover";
            return (
              <Fragment key={item.href}>
                <Link
                  href={item.href}
                  onClick={onClose}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item ${active ? "sidebar-nav-item-active" : ""}`}
                >
                  <span className="w-5 flex items-center justify-center shrink-0">
                    {item.renderIcon ? item.renderIcon() : item.icon && <item.icon size={16} strokeWidth={1.5} />}
                  </span>
                  <span className="truncate">{item.label}</span>
                  {item.badge != null && item.badge > 0 && (
                    <span
                      className="ml-auto text-white text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1"
                      style={badgeAccentStyle}
                    >
                      {item.badge > 99 ? "99+" : item.badge}
                    </span>
                  )}
                </Link>
                {idx === 0 && (
                  <button
                    className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-[13px] font-medium sidebar-nav-item"
                    onClick={() => { onClose(); window.dispatchEvent(new Event("openCreate")); }}
                  >
                    <span className="w-5 flex items-center justify-center shrink-0"><SquarePen size={16} strokeWidth={1.5} /></span>
                    <span className="truncate">Create</span>
                  </button>
                )}
                {idx === 0 && (
                  <Link
                    href="/search"
                    onClick={onClose}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item ${searchActive ? "sidebar-nav-item-active" : ""}`}
                  >
                    <span className="w-5 flex items-center justify-center shrink-0">
                      <Search size={16} strokeWidth={1.5} />
                    </span>
                    <span className="truncate">Search</span>
                  </Link>
                )}
              </Fragment>
            );
          })}

          {/* Drafts (conditional) */}
          {draftCount > 0 && (
            <button
              className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-[13px] font-medium sidebar-nav-item"
              onClick={() => { onClose(); window.dispatchEvent(new Event("openComposeWithDrafts")); }}
            >
              <span className="w-5 flex items-center justify-center shrink-0"><FileText size={16} strokeWidth={1.5} /></span>
              <span className="truncate">Drafts</span>
              <span className="ml-auto bg-(--scroll-text) text-(--scroll-bg) text-[10px] font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                {draftCount > 99 ? "99+" : draftCount}
              </span>
            </button>
          )}

          {/* Admin (conditional) */}
          {user?.isAdmin && (
            <Link
              href="/admin/usage"
              onClick={onClose}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[13px] font-medium cursor-pointer sidebar-nav-item ${location.startsWith("/admin") ? "sidebar-nav-item-active" : ""}`}
            >
              <span className="w-5 flex items-center justify-center shrink-0"><Shield size={16} strokeWidth={1.5} /></span>
              <span className="truncate">Admin</span>
            </Link>
          )}
        </nav>

        {/* User row */}
        {user && (() => {
          const hasDistinctDisplayName = !!user.displayName && user.displayName !== user.username;
          const primary = hasDistinctDisplayName
            ? user.displayName
            : user.username
              ? `@${user.username}`
              : "";
          return (
            <div className="flex items-center gap-2 px-3 py-2.5 border-t border-(--scroll-border)">
              <Link
                href={profilePath}
                onClick={onClose}
                className="flex items-center gap-2 flex-1 min-w-0 px-2 py-1 rounded-lg sidebar-nav-item"
              >
                <Avatar className="h-6 w-6 shrink-0">
                  <AvatarImage src={user.profileImageUrl ?? undefined} alt="" />
                  <AvatarFallback className="text-[10px]">{fallbackInitial}</AvatarFallback>
                </Avatar>
                <span className="text-[13px] font-medium text-(--scroll-text) truncate">
                  {primary}
                </span>
              </Link>
              <button
                onClick={() => { onClose(); signOut({ redirectUrl: '/' }); }}
                aria-label="Log out"
                className="shrink-0 w-7 h-7 flex items-center justify-center rounded-md text-(--scroll-text-tertiary) hover:text-(--scroll-text) hover:bg-(--scroll-bg-secondary) transition-colors"
              >
                <LogOut size={14} strokeWidth={1.5} />
              </button>
            </div>
          );
        })()}
      </aside>
    </>
  );
}
